import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    FluentProvider,
    Button
} from "@fluentui/react-components";
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from "react";
import ThemeUtils from "../../../utility/ThemeUtils";

const styles = require("./NewShiftsDialog.scss");
const classNames = require("classnames/bind");

interface NewShiftsDialogProps {
  title: React.ReactNode;
  subtext?: string;
  content: React.ReactNode;
  actions: React.ReactNode;
  isOpen: boolean;
  onDismiss: () => void;
  modalType?: "modal" | "non-modal";
  ariaDescribedBy?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  closeOnOutsideClick?: boolean;
  /** When true, the dismiss button is rendered after DialogContent in the DOM so it
   *  appears last in the tab order. The button is absolutely positioned to stay
   *  visually in the top-right corner. The consumer must add `position: relative`
   *  to their `customStyles.dialogBodyContent` class. */
  dismissAfterContent?: boolean;
  customStyles?: {
    dialogSurface?: string;
    dialogBody?: string;
    dialogActions?: string;
    dialogTitle?: string;
    dialogContent?: string;
    dialogSubtext?: string;
    dialogBodyContent?: string;
  };
  showDismiss?: boolean;
}

export const NewShiftsDialog: React.FC<NewShiftsDialogProps> = ({
    title,
    subtext,
    content,
    actions,
    isOpen,
    onDismiss,
    modalType = "modal",
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
    closeOnOutsideClick = false,
    dismissAfterContent = false,
    customStyles = {
        dialogSurface: "",
        dialogBody: "",
        dialogActions: "",
        dialogTitle: "",
        dialogContent: "",
        dialogSubtext: "",
        dialogBodyContent: ""
    },
    showDismiss = false
}) => {
    const handleOpenChange = (event: any) => {
        // Only dismiss if closeOnOutsideClick is true or it's not an outside click
        if (closeOnOutsideClick || event.type !== "click") {
            onDismiss();
        }
    };
    return (
        // This div is used to prevent the focus leaving the dialog when the dialog is open
        <div onKeyDown={e => e.stopPropagation()}>
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
                <Dialog modalType={modalType} onOpenChange={handleOpenChange} open={isOpen}>
                    <DialogSurface
                        {...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {})}
                        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
                        {...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {})}
                        className={classNames(styles.dialogSurface, customStyles.dialogSurface)}>
                        <DialogBody className={classNames(styles.dialogBody, customStyles.dialogBody)}>
                            <div className={customStyles.dialogBodyContent}>
                                <div className={classNames(styles.headerContainer, customStyles.dialogTitle)}>
                                    <div className={styles.dialogTitleContainer}>
                                        {title !== undefined && title !== null && (
                                            <DialogTitle>
                                                {title}
                                            </DialogTitle>
                                        )}
                                        {subtext && <div className={customStyles.dialogSubtext}>{subtext}</div>}
                                    </div>
                                    {showDismiss && !dismissAfterContent && (
                                        <div className={styles.dialogActionButton}>
                                            <DialogTrigger action="close">
                                                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} onClick={() => onDismiss()} />
                                            </DialogTrigger>
                                        </div>
                                    )}
                                </div>
                                <DialogContent>{content}</DialogContent>
                                {showDismiss && dismissAfterContent && (
                                    <div className={styles.dialogActionButtonAbsolute}>
                                        <DialogTrigger action="close">
                                            <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} onClick={() => onDismiss()} />
                                        </DialogTrigger>
                                    </div>
                                )}
                            </div>
                            <DialogActions className={classNames(styles.dialogActions, customStyles.dialogActions)}>
                                {actions}
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </FluentProvider>
        </div>
    );
};